/**
 * HistoryView — Admin-only history tab showing all inventory records
 * with filters for type, location, storage area, dates.
 */
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { MagnifyingGlass, X, Pencil, Trash, ClockCounterClockwise } from "@phosphor-icons/react";

export function HistoryView({
  historyItemType, setHistoryItemType,
  historyLocation, setHistoryLocation,
  historyStorageArea, setHistoryStorageArea,
  historyStorageAreas,
  historyStartDate, setHistoryStartDate,
  historyEndDate, setHistoryEndDate,
  historySearch, setHistorySearch,
  historyLoading, historyRecords, filteredHistoryRecords,
  locations, fetchHistory,
  onEditRecord, onDeleteRecord
}) {
  return (
    <div className="mt-4 space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="w-full sm:w-36">
          <Label className="text-sm text-muted-foreground mb-1 block">Type</Label>
          <Select value={historyItemType} onValueChange={setHistoryItemType}>
            <SelectTrigger data-testid="history-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              <SelectItem value="product">Products Only</SelectItem>
              <SelectItem value="prep">Prep Items Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-44">
          <Label className="text-sm text-muted-foreground mb-1 block">Location</Label>
          <Select value={historyLocation} onValueChange={setHistoryLocation}>
            <SelectTrigger data-testid="history-location-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map(loc => (
                <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {historyLocation !== "all" && historyStorageAreas.length > 0 && (
          <div className="w-full sm:w-44">
            <Label className="text-sm text-muted-foreground mb-1 block">Storage Area</Label>
            <Select value={historyStorageArea} onValueChange={setHistoryStorageArea}>
              <SelectTrigger data-testid="history-storage-area-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {historyStorageAreas.map(area => (
                  <SelectItem key={area.area_id} value={area.area_id}>{area.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="w-full sm:w-36">
          <Label className="text-sm text-muted-foreground mb-1 block">From</Label>
          <Input type="date" value={historyStartDate} onChange={(e) => setHistoryStartDate(e.target.value)} data-testid="history-start-date" />
        </div>
        <div className="w-full sm:w-36">
          <Label className="text-sm text-muted-foreground mb-1 block">To</Label>
          <Input type="date" value={historyEndDate} onChange={(e) => setHistoryEndDate(e.target.value)} data-testid="history-end-date" />
        </div>
        <div className="flex items-end">
          <Button variant="outline" onClick={fetchHistory} data-testid="history-search-btn">
            <MagnifyingGlass size={16} className="mr-2" /> Search
          </Button>
        </div>
      </div>

      {/* Search */}
      {historyRecords.length > 0 && (
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="history-product-search"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            placeholder="Filter by item name, location, storage area, or user..."
            className="pl-10 pr-10"
          />
          {historySearch && (
            <button onClick={() => setHistorySearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X size={18} />
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {historyLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredHistoryRecords.length > 0 ? (
        <Card className="border rounded-xl">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Storage Area</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHistoryRecords.map(rec => (
                    <TableRow key={rec.record_id} data-testid={`history-row-${rec.record_id}`}>
                      <TableCell className="text-sm whitespace-nowrap">{rec.recorded_at?.slice(0, 10)}</TableCell>
                      <TableCell className="font-medium max-w-[180px] truncate">{rec.item_name || rec.product_name}</TableCell>
                      <TableCell>
                        <Badge variant={rec.item_type === "prep" ? "secondary" : "outline"} className="text-[10px]">
                          {rec.item_type === "prep" ? "PREP" : "PROD"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{rec.location_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{rec.storage_area_name || "-"}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{rec.quantity?.toFixed(2)}</TableCell>
                      <TableCell className="text-right tabular-nums">${rec.price_at_count?.toFixed(2)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-primary">${rec.value?.toFixed(2)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{rec.recorded_by}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEditRecord(rec)} data-testid={`edit-record-${rec.record_id}`}>
                            <Pencil size={14} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDeleteRecord(rec)} data-testid={`delete-record-${rec.record_id}`}>
                            <Trash size={14} className="text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border rounded-xl">
          <CardContent className="py-16 text-center">
            <ClockCounterClockwise size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No inventory records found</p>
            <p className="text-sm text-muted-foreground mt-1">Select a date range and location to view history</p>
          </CardContent>
        </Card>
      )}
      <p className="text-sm text-muted-foreground">
        {historySearch ? `${filteredHistoryRecords.length} of ${historyRecords.length}` : historyRecords.length} records
      </p>
    </div>
  );
}
